import { Countdown } from "@/components/site/Countdown";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { api, img } from "@/lib/api";
import { ProductCard } from "@/components/site/ProductCard";
import { Logo } from "@/components/site/Logo";
import useSeo from "@/lib/useSeo";

const Hero = ({ s }) => (
  <section data-testid="hero-section" className="grid lg:grid-cols-12 border-b border-[var(--mm-line)]">
    <div className="lg:col-span-7 relative h-[62vh] lg:h-[calc(100vh-8rem)] min-h-[440px] overflow-hidden bg-[var(--mm-sand)]">
      {s.hero_image && <img data-testid="hero-image" src={img(s.hero_image)} alt="Maison Madison campaign" className="absolute inset-0 w-full h-full object-cover scale-[1.02]" />}
      <span className="absolute bottom-5 left-5 eyebrow bg-[var(--mm-cream)] text-[var(--mm-red)] px-3 py-1.5">Campaign — Chapter One</span>
    </div>
    <div className="lg:col-span-5 flex flex-col justify-between px-6 sm:px-12 py-12 lg:py-16 gap-12">
      <p className="eyebrow text-[var(--mm-red)] rise">{s.hero_eyebrow}</p>
      <div>
        <Logo className="w-full max-w-[420px] h-auto rise d1" testId="hero-logo-img" />
        <h1 className="font-serif-mm text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-10 rise d2">{s.hero_title}</h1>
        <p className="mt-6 text-sm sm:text-base leading-relaxed text-[var(--mm-muted)] max-w-md rise d3">{s.hero_subtitle}</p>
      </div>
      <div className="flex flex-wrap gap-3 rise d4">
        <Link to="/shop" data-testid="hero-shop-now-button" className="btn-mm">Shop Now <ArrowUpRight size={15} /></Link>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const words = Array.from({ length: 8 }, (_, i) => <span key={i} className="mx-8">maison madison <em className="opacity-70">· since 2025 ·</em></span>);
  return (
    <div className="bg-[var(--mm-red)] text-[var(--mm-cream)] overflow-hidden py-4 font-serif-mm text-2xl sm:text-3xl whitespace-nowrap">
      <div className="marquee-track inline-flex">{words}{words}</div>
    </div>
  );
};

const Intro = ({ s }) => (
  <section data-testid="brand-intro-section" className="px-6 sm:px-10 lg:px-16 py-20 sm:py-28 lg:py-36 grid lg:grid-cols-12 gap-10">
    <p className="eyebrow text-[var(--mm-red)] lg:col-span-3">The House</p>
    <div className="lg:col-span-8">
      <h2 className="font-serif-mm text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{s.intro_title}</h2>
      <p className="mt-8 text-sm sm:text-base leading-relaxed text-[var(--mm-muted)] max-w-2xl">{s.intro_text}</p>
      <Link to="/about" data-testid="intro-read-more-link" className="inline-block mt-8 nav-link text-[var(--mm-red)]">Discover Maison Madison</Link>
    </div>
  </section>
);

const Featured = ({ s, product }) => (
  <section data-testid="featured-collection-section" className="px-6 sm:px-10 lg:px-16 pb-20 sm:pb-28">
    <div className="flex items-end justify-between mb-10 border-t border-[var(--mm-line)] pt-8">
      <div><p className="eyebrow text-[var(--mm-red)] mb-3">Featured collection</p><h2 className="font-serif-mm text-3xl sm:text-5xl">The Leather <em>Edit</em></h2></div>
      <Link to="/shop" data-testid="featured-view-all-link" className="nav-link hidden sm:inline">View all</Link>
    </div>
    <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
      <div className="lg:col-span-7 aspect-[4/5] overflow-hidden bg-[var(--mm-sand)] img-zoom">
        {s.editorial_image && <img src={img(s.editorial_image)} alt="Featured collection" className="w-full h-full object-cover" />}
      </div>
      <div className="lg:col-span-4 lg:col-start-9 lg:pt-24">
        {product && <ProductCard product={product} />}
        <p className="mt-8 text-sm leading-relaxed text-[var(--mm-muted)]">Cut from vintage-finish leather with hand-laced detailing — pieces designed to be worn in, not worn out.</p>
      </div>
    </div>
  </section>
);

const NewArrivals = ({ products }) => (
  <section data-testid="new-arrivals-section" className="px-6 sm:px-10 lg:px-16 py-20 sm:py-28 bg-[var(--mm-sand)]">
    <div className="flex items-end justify-between mb-12">
      <div><p className="eyebrow text-[var(--mm-red)] mb-3">Just landed</p><h2 className="font-serif-mm text-3xl sm:text-5xl">New <em>Arrivals</em></h2></div>
      <Link to="/shop?new=1" data-testid="new-arrivals-view-all-link" className="nav-link">Shop new</Link>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-12">
      {products.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
    </div>
  </section>
);

export default function Home() {
  useSeo(null);
  const { settings } = useShop();
  const [featured, setFeatured] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/products", { params: { new: true } }).then((r) => setArrivals(r.data));
  }, []);
  const s = settings || {};
  return (
    <div data-testid="home-page">
      <Hero s={s} />
      <Countdown to={s?.launch_at} title={s?.launch_title} />
      <Marquee />
      <Intro s={s} />
      <Featured s={s} product={featured[0]} />
      <NewArrivals products={arrivals} />
    </div>
  );
}
